import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  type OrderDraftMutationStatusEnum,
  OrderDraftMutationStatusStatuses,
} from './OrderDraftMutationStatusEnum'

export type OrderDraftMutationVisibleStatus = Exclude<
  OrderDraftMutationStatusEnum,
  typeof OrderDraftMutationStatusStatuses.COMPLETED
>

interface OrderDraftMutationStatusDetails {
  status: OrderDraftMutationVisibleStatus
  latestMutationSeq: number
}

interface IOrderDraftMutationStatusContextValue {
  getMutationStatus: () => OrderDraftMutationVisibleStatus | null
  setMutationStatus: (
    status: OrderDraftMutationStatusEnum,
    mutationSeq?: number
  ) => number | null
  /**
   * Resolves once the order-draft mutation is no longer in flight (i.e. the
   * status has transitioned out of UPDATING into either COMPLETED or
   * FAILED_TO_UPDATE). Resolves immediately when nothing is in flight.
   */
  waitForMutationsSettled: () => Promise<void>
  /**
   * Returns true if the order-draft mutation is currently in FAILED_TO_UPDATE.
   * Reads the latest committed state synchronously, so it's safe to call
   * after `await waitForMutationsSettled()` without closure-staleness.
   */
  hasFailedMutation: () => boolean
}

export const OrderDraftMutationStatusContext =
  createContext<IOrderDraftMutationStatusContextValue | null>(null)

export const OrderDraftMutationStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [mutationDetails, setMutationDetails] =
    useState<OrderDraftMutationStatusDetails | null>(null)

  // Monotonically increasing counter for mutation ordering.
  // Lives in a ref so setMutationStatus can return the
  // assigned sequence synchronously.
  const seqCounterRef = useRef(0)

  // Resolvers for callers awaiting waitForMutationsSettled. Drained in an
  // effect on the render where the status transitions out of UPDATING.
  const waitersRef = useRef<(() => void)[]>([])

  const isInFlight =
    mutationDetails?.status === OrderDraftMutationStatusStatuses.UPDATING

  // Mirror of isInFlight in a ref so waitForMutationsSettled can stay stable
  // across renders while still reading the latest committed value.
  const isInFlightRef = useRef(isInFlight)
  isInFlightRef.current = isInFlight

  const isFailed =
    mutationDetails?.status ===
    OrderDraftMutationStatusStatuses.FAILED_TO_UPDATE

  // Mirror of isFailed in a ref so hasFailedMutation can stay stable across
  // renders while still reading the latest committed value — important as
  // this may be called from a closure.
  const isFailedRef = useRef(isFailed)
  isFailedRef.current = isFailed

  useEffect(() => {
    if (!isInFlight && waitersRef.current.length > 0) {
      const toResolve = waitersRef.current
      waitersRef.current = []
      toResolve.forEach((resolve) => resolve())
    }
  }, [isInFlight])

  const getMutationStatus =
    useCallback((): OrderDraftMutationVisibleStatus | null => {
      return mutationDetails?.status ?? null
    }, [mutationDetails])

  const setMutationStatus = useCallback(
    (
      status: OrderDraftMutationStatusEnum,
      mutationSeq?: number
    ): number | null => {
      const currentSeq =
        status === OrderDraftMutationStatusStatuses.UPDATING
          ? ++seqCounterRef.current
          : null

      setMutationDetails((prev) => {
        if (status === OrderDraftMutationStatusStatuses.UPDATING) {
          // This should never happen, but we'll add a safety check just in case.
          if (typeof currentSeq !== 'number') {
            console.error('Unexpected sequence value:', currentSeq)
            return prev
          }
          return {
            status,
            latestMutationSeq: currentSeq,
          }
        }

        // Only the latest mutation can set FAILED_TO_UPDATE; a stale failure
        // must not resurrect error state that was already cleared by a later success.
        if (
          status === OrderDraftMutationStatusStatuses.FAILED_TO_UPDATE &&
          prev &&
          mutationSeq === prev.latestMutationSeq
        ) {
          return {
            status,
            latestMutationSeq: mutationSeq ?? 0,
          }
        }

        // COMPLETED can clear state even when prev is null (already cleared by
        // a concurrent success), but still only acts on the latest sequence.
        if (
          status === OrderDraftMutationStatusStatuses.COMPLETED &&
          (!prev || mutationSeq === prev.latestMutationSeq)
        ) {
          return null
        }

        // Current mutation is stale; return previous details
        // and wait for latest mutation to complete or fail.
        return prev
      })

      return currentSeq
    },
    []
  )

  const waitForMutationsSettled = useCallback((): Promise<void> => {
    if (!isInFlightRef.current) return Promise.resolve()
    return new Promise<void>((resolve) => {
      waitersRef.current.push(resolve)
    })
  }, [])

  const hasFailedMutation = useCallback((): boolean => {
    return isFailedRef.current
  }, [])

  const contextValue = useMemo(
    () => ({
      getMutationStatus,
      setMutationStatus,
      waitForMutationsSettled,
      hasFailedMutation,
    }),
    [
      getMutationStatus,
      setMutationStatus,
      waitForMutationsSettled,
      hasFailedMutation,
    ]
  )

  return (
    <OrderDraftMutationStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftMutationStatusContext.Provider>
  )
}
